/* eslint-disable @next/next/no-img-element */
import React, { useState } from 'react';
import CustomTooltip from '@/components/common/Tooltip';
import CommonJs from '@/utils/commonJs';
import { useLocalFilter } from '@/hooks/useOrganizationIndexedDBFilter';

export interface EmployeeInfo {
    id?: string;
    tenNhanVien?: string;
    maNhanVien?: string;
  hinh?: string;
  boPhan?: string;
  chucVu?: string;
  fullLabel?: string;
}

export interface EmployeeAvatarProps {
  employeeInfo: EmployeeInfo | null;
  size?: number;
  disableTooltip?: boolean;
}

/**
 * Helper function to get initials from employee name
 */
export const getInitialsFromEmployee = (
  employeeInfo: EmployeeInfo | null,
  getEmployeeOrDepartmentAbbrev?: (id: string) => { abbr: string; useToAbbrev: boolean },
  ready?: boolean
): string => {
  if (!employeeInfo) return '';

  if (employeeInfo.tenNhanVien) {
    const name = (employeeInfo.tenNhanVien || '').trim();
    if (!name) return '?';

    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) || '') : '';
    return (first + last).toUpperCase() || '?';
  }

  if (employeeInfo.id && ready && getEmployeeOrDepartmentAbbrev) {
    const abbrevResult = getEmployeeOrDepartmentAbbrev(employeeInfo.id);
    return abbrevResult.abbr || '';
  }

  return '';
};

/**
 * EmployeeAvatar Component - Hiển thị avatar của nhân viên
 * @param employeeInfo - Thông tin nhân viên
 * @param size - Kích thước avatar (mặc định: 28)
 */
export const EmployeeAvatar: React.FC<EmployeeAvatarProps> = ({ employeeInfo, size = 28, disableTooltip = false }) => {
  const [imageError, setImageError] = useState(false);
  const { ready, getEmployeeOrDepartmentAbbrev } = useLocalFilter();

  if (!employeeInfo) {
    const missingUserAvatar = (
      <div
        className="rounded-full flex items-center justify-center font-semibold employee-avatar-hover-transparent"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
          flexShrink: 0,
          cursor: 'default',
          backgroundColor: '#F2F4F7',
          color: '#98A2B3',
          fontSize: Math.floor(size * 0.42),
        }}
        aria-label="User không tồn tại"
      >
        ?
      </div>
    );
    if (disableTooltip) return missingUserAvatar;

    return (
      <CustomTooltip value="User không tồn tại">
        {missingUserAvatar}
      </CustomTooltip>
    );
  }

  let initials = getInitialsFromEmployee(employeeInfo, getEmployeeOrDepartmentAbbrev, ready);

  if (!initials && employeeInfo.id && ready) {
    const abbrevResult = getEmployeeOrDepartmentAbbrev(employeeInfo.id);
    initials = abbrevResult.abbr || '';
  }

  const tooltipContent = [
    employeeInfo.tenNhanVien ? `<strong>Tên:</strong> ${employeeInfo.tenNhanVien}` : '',
    employeeInfo.boPhan ? `<strong>Bộ phận:</strong> ${employeeInfo.boPhan}` : '',
    employeeInfo.chucVu ? `<strong>Chức vụ:</strong> ${employeeInfo.chucVu}` : '',
  ]
    .filter(Boolean)
    .join('<br/>');

  const shouldShowImage = employeeInfo.hinh && !imageError;
  const backgroundColor = shouldShowImage
    ? undefined
    : CommonJs.genColorByName(employeeInfo.tenNhanVien || employeeInfo.id || '');
  const avatarContent = (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold employee-avatar-hover-transparent"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        flexShrink: 0,
        cursor: 'pointer',
        backgroundColor: employeeInfo.hinh ? '#ffffff' : backgroundColor,
        fontSize: Math.floor(size * 0.42),
        lineHeight: 1,
      }}
    >
      {shouldShowImage ? (
        <img
          src={employeeInfo.hinh}
          alt={employeeInfo.tenNhanVien || initials}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
          onError={() => {
            setImageError(true);
          }}
        />
      ) : (
        initials || ''
      )}
    </div>
  );

  return (
    <>
      {disableTooltip ? avatarContent : <CustomTooltip value={tooltipContent}>{avatarContent}</CustomTooltip>}
    </>
  );
};

export default EmployeeAvatar;
